import { ClipboardCheck, CheckCircle2, History, Send } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { PortalData } from '@/types/portalCliente'
import type { PortalClienteState } from '@/types/portalClienteState'

type Props = { data: PortalData; portal: PortalClienteState }

export function PortalClienteCheckinSection({ data, portal }: Props) {
  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
      <h2 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
        <ClipboardCheck size={14} />
        Check-in Periódico
      </h2>
      {portal.checkinEnviado ? (
        <div className="flex items-center gap-3 py-4">
          <CheckCircle2 size={24} className="text-emerald-400" />
          <div>
            <p className="text-sm font-medium text-white">Check-in registrado!</p>
            <p className="text-xs text-white/40">Obrigado por atualizar seu status.</p>
          </div>
        </div>
      ) : !portal.showCheckinForm ? (
        <div>
          <p className="text-sm text-white/60 mb-3">
            Como está sendo sua experiência com nossos serviços? Faça um check-in rápido.
          </p>
          <button
            type="button"
            onClick={() => portal.setShowCheckinForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/80 transition-all"
          >
            <ClipboardCheck size={14} />
            Fazer Check-in
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-sm text-white/70 mb-3">De 1 a 10, como avalia o serviço atualmente?</p>
            <div className="flex flex-wrap gap-1.5">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => portal.setCheckinNota(n)}
                  className={cn(
                    'w-9 h-9 rounded-lg text-sm font-bold transition-all border',
                    portal.checkinNota === n
                      ? n >= 9
                        ? 'bg-emerald-500 text-white border-emerald-400 scale-110'
                        : n >= 7
                          ? 'bg-yellow-500 text-white border-yellow-400 scale-110'
                          : 'bg-red-500 text-white border-red-400 scale-110'
                      : 'bg-white/[0.04] text-white/60 border-white/[0.06] hover:border-accent/50',
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={portal.checkinComentario}
            onChange={(e) => portal.setCheckinComentario(e.target.value)}
            placeholder="Conte como está sendo a experiência (opcional)"
            rows={3}
            className="w-full bg-white/[0.04] border border-white/[0.06] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-accent resize-none placeholder:text-white/30"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={portal.handleCheckinSubmit}
              disabled={portal.checkinEnviando}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/80 transition-all disabled:opacity-50"
            >
              <Send size={14} />
              {portal.checkinEnviando ? 'Enviando...' : 'Registrar Check-in'}
            </button>
            <button
              type="button"
              onClick={() => portal.setShowCheckinForm(false)}
              className="px-4 py-2 rounded-lg text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      {data.checkins && data.checkins.length > 0 && (
        <div className="pt-3 border-t border-white/[0.06] space-y-2">
          <p className="text-xs text-white/40 uppercase tracking-wider font-medium flex items-center gap-1.5">
            <History size={11} />
            Últimos check-ins
          </p>
          {data.checkins.slice(0, 3).map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-emerald-400" />
                <div>
                  {c.descricao && <p className="text-xs text-white/70">{c.descricao}</p>}
                  <span className="text-[10px] text-white/30">
                    {new Date(c.data).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
